/**
 * Reports Board - Notion-style daily digests and pinned posts
 *
 * Team knowledge sharing with pinning and posting
 */

import { useState } from 'react'
import {
  Pin,
  Plus,
  Search,
  Calendar,
  MessageSquare,
  Heart,
  Bookmark,
  Sparkles,
  TrendingUp,
  AlertCircle,
} from 'lucide-react'
import { cn } from '../lib/utils'

type PostType = 'digest' | 'report' | 'announcement' | 'discussion'

interface Post {
  id: string
  title: string
  content: string
  type: PostType
  author: string
  avatar: string
  date: string
  pinned: boolean
  likes: number
  comments: number
  liked?: boolean
  bookmarked?: boolean
}

const POST_TYPES: { value: PostType; label: string; icon: typeof Sparkles; color: string }[] = [
  { value: 'digest', label: 'Daily Digest', icon: Sparkles, color: 'text-lambda-gold bg-lambda-gold/10' },
  { value: 'report', label: 'Report', icon: TrendingUp, color: 'text-blue-400 bg-blue-500/10' },
  { value: 'announcement', label: 'Announcement', icon: AlertCircle, color: 'text-purple-400 bg-purple-500/10' },
  { value: 'discussion', label: 'Discussion', icon: MessageSquare, color: 'text-emerald-400 bg-emerald-500/10' },
]

const MOCK_POSTS: Post[] = [
  {
    id: '1',
    title: 'Daily Digest - November 30, 2024',
    content: 'Today we shipped the new Calendar, Tasks, and Meeting Room features for lukhas.team. The team also completed 12 PRs and closed 8 issues. Great progress on the Constellation Framework documentation.',
    type: 'digest',
    author: 'LUKHAS Bot',
    avatar: 'LB',
    date: 'Today',
    pinned: true,
    likes: 15,
    comments: 3,
  },
  {
    id: '2',
    title: 'Q4 Performance Report',
    content: 'System performance metrics for Q4. API latency down 23%, test coverage up to 89%, and zero critical incidents this quarter. Full report attached.',
    type: 'report',
    author: 'Maya Patel',
    avatar: 'MP',
    date: 'Yesterday',
    pinned: true,
    likes: 24,
    comments: 7,
  },
  {
    id: '3',
    title: 'New Team Member Welcome',
    content: 'Please welcome Hans Mueller to the team! Hans joins us as a consultant specializing in consciousness research. He\'ll be working closely with the Identity Star team.',
    type: 'announcement',
    author: 'Alex Chen',
    avatar: 'AC',
    date: '2 days ago',
    pinned: false,
    likes: 32,
    comments: 12,
  },
  {
    id: '4',
    title: 'Should we adopt the new testing framework?',
    content: 'I\'ve been evaluating Vitest as a replacement for Jest. Initial benchmarks show 3x faster execution. Thoughts?',
    type: 'discussion',
    author: 'Carlos Ruiz',
    avatar: 'CR',
    date: '3 days ago',
    pinned: false,
    likes: 8,
    comments: 15,
  },
  {
    id: '5',
    title: 'Daily Digest - November 29, 2024',
    content: 'Simplified all UX components with Steve Jobs aesthetic. Reduced code by 50% while maintaining functionality. Build passing with 0 errors in new components.',
    type: 'digest',
    author: 'LUKHAS Bot',
    avatar: 'LB',
    date: '3 days ago',
    pinned: false,
    likes: 18,
    comments: 4,
  },
]

export default function ReportsBoard() {
  const [posts, setPosts] = useState(MOCK_POSTS)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<PostType | 'all'>('all')
  const [showNewPost, setShowNewPost] = useState(false)

  const filtered = posts
    .filter(p => filter === 'all' || p.type === filter)
    .filter(p =>
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.content.toLowerCase().includes(search.toLowerCase())
    )

  const pinned = filtered.filter(p => p.pinned)
  const unpinned = filtered.filter(p => !p.pinned)

  const togglePin = (id: string) => {
    setPosts(prev => prev.map(p =>
      p.id === id ? { ...p, pinned: !p.pinned } : p
    ))
  }

  const toggleLike = (id: string) => {
    setPosts(prev => prev.map(p =>
      p.id === id ? { ...p, liked: !p.liked, likes: p.liked ? p.likes - 1 : p.likes + 1 } : p
    ))
  }

  const toggleBookmark = (id: string) => {
    setPosts(prev => prev.map(p =>
      p.id === id ? { ...p, bookmarked: !p.bookmarked } : p
    ))
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light text-white">Reports & Digests</h1>
          <p className="text-gray-500 mt-1">Team updates and knowledge sharing</p>
        </div>
        <button
          onClick={() => setShowNewPost(true)}
          className="flex items-center gap-2 px-4 py-2 bg-lambda-gold text-black rounded-lg hover:bg-lambda-gold/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span className="text-sm font-medium">New post</span>
        </button>
      </div>

      {/* Filters & Search */}
      <div className="flex items-center gap-4">
        {/* Type Filter */}
        <div className="flex gap-1 bg-white/5 p-1 rounded-lg">
          <button
            onClick={() => setFilter('all')}
            className={cn(
              'px-3 py-1.5 text-sm rounded-md transition-colors',
              filter === 'all' ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'
            )}
          >
            All
          </button>
          {POST_TYPES.map(t => (
            <button
              key={t.value}
              onClick={() => setFilter(t.value)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition-colors',
                filter === t.value ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search posts..."
            className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 outline-none focus:border-white/20"
          />
        </div>
      </div>

      {/* Pinned Section */}
      {pinned.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Pin className="w-4 h-4 text-lambda-gold" />
            <span className="text-sm text-lambda-gold font-medium">Pinned</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {pinned.map(post => (
              <PostCard
                key={post.id}
                post={post}
                onTogglePin={() => togglePin(post.id)}
                onToggleLike={() => toggleLike(post.id)}
                onToggleBookmark={() => toggleBookmark(post.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recent Posts */}
      <div>
        <h2 className="text-sm text-gray-500 uppercase tracking-wider mb-4">Recent</h2>
        <div className="space-y-4">
          {unpinned.map(post => (
            <PostCard
              key={post.id}
              post={post}
              onTogglePin={() => togglePin(post.id)}
              onToggleLike={() => toggleLike(post.id)}
              onToggleBookmark={() => toggleBookmark(post.id)}
              compact
            />
          ))}
        </div>
      </div>

      {/* New Post Modal */}
      {showNewPost && (
        <NewPostModal
          onClose={() => setShowNewPost(false)}
          onPost={(post) => {
            setPosts(prev => [post, ...prev])
            setShowNewPost(false)
          }}
        />
      )}
    </div>
  )
}

function PostCard({
  post,
  onTogglePin,
  onToggleLike,
  onToggleBookmark,
  compact = false
}: {
  post: Post
  onTogglePin: () => void
  onToggleLike: () => void
  onToggleBookmark: () => void
  compact?: boolean
}) {
  const typeInfo = POST_TYPES.find(t => t.value === post.type)!

  return (
    <div className={cn(
      'p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors',
      post.pinned && 'border-lambda-gold/20'
    )}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium',
            post.author === 'LUKHAS Bot' ? 'bg-lambda-gold/20 text-lambda-gold' : 'bg-white/10 text-white'
          )}>
            {post.avatar}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-white font-medium">{post.author}</span>
              <span className={cn('text-xs px-2 py-0.5 rounded-full', typeInfo.color)}>
                {typeInfo.label}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
              <Calendar className="w-3 h-3" />
              {post.date}
            </div>
          </div>
        </div>
        <button
          onClick={onTogglePin}
          className={cn(
            'p-2 rounded-lg transition-colors',
            post.pinned ? 'text-lambda-gold bg-lambda-gold/10' : 'text-gray-500 hover:text-white hover:bg-white/10'
          )}
        >
          <Pin className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <h3 className="text-lg text-white mb-2">{post.title}</h3>
      <p className={cn(
        'text-gray-400 text-sm',
        compact && 'line-clamp-2'
      )}>
        {post.content}
      </p>

      {/* Actions */}
      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/5">
        <button
          onClick={onToggleLike}
          className={cn(
            'flex items-center gap-1.5 text-sm transition-colors',
            post.liked ? 'text-red-400' : 'text-gray-500 hover:text-white'
          )}
        >
          <Heart className={cn('w-4 h-4', post.liked && 'fill-current')} />
          {post.likes}
        </button>
        <button className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-white transition-colors">
          <MessageSquare className="w-4 h-4" />
          {post.comments}
        </button>
        <div className="flex-1" />
        <button
          onClick={onToggleBookmark}
          className={cn(
            'p-1.5 rounded-lg transition-colors',
            post.bookmarked ? 'text-lambda-gold' : 'text-gray-500 hover:text-white'
          )}
        >
          <Bookmark className={cn('w-4 h-4', post.bookmarked && 'fill-current')} />
        </button>
      </div>
    </div>
  )
}

function NewPostModal({
  onClose,
  onPost
}: {
  onClose: () => void
  onPost: (post: Post) => void
}) {
  const [form, setForm] = useState({
    title: '',
    content: '',
    type: 'discussion' as PostType
  })

  const handleSubmit = () => {
    if (!form.title || !form.content) return
    onPost({
      id: Date.now().toString(),
      ...form,
      author: 'Alex Chen',
      avatar: 'AC',
      date: 'Just now',
      pinned: false,
      likes: 0,
      comments: 0,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#0a0a0f] border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl text-white mb-6">Create new post</h2>

        <div className="space-y-4">
          {/* Type Selection */}
          <div className="flex gap-2">
            {POST_TYPES.map(t => {
              const Icon = t.icon
              return (
                <button
                  key={t.value}
                  onClick={() => setForm(prev => ({ ...prev, type: t.value }))}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                    form.type === t.value ? t.color : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              )
            })}
          </div>

          {/* Title */}
          <input
            type="text"
            value={form.title}
            onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Post title..."
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-lg text-white placeholder-gray-500 outline-none focus:border-white/20"
          />

          {/* Content */}
          <textarea
            value={form.content}
            onChange={e => setForm(prev => ({ ...prev, content: e.target.value }))}
            placeholder="Write your post..."
            rows={6}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 outline-none focus:border-white/20 resize-none"
          />
        </div>

        <div className="flex gap-3 mt-8">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-lambda-gold text-black rounded-lg font-medium hover:bg-lambda-gold/90 transition-colors"
          >
            Publish
          </button>
        </div>
      </div>
    </div>
  )
}
